import apiClient from '../core';
import type {
  Component,
  ComponentType,
  LibraryComponent,
  CreateSharedComponentRequest,
  UpdateSharedComponentRequest,
  SearchSharedComponentsRequest,
  LibraryBrowseResponse,
} from '../../schemas/validation';

export const libraryApi = {
  // Browse component library
  async browseComponents(params?: SearchSharedComponentsRequest): Promise<LibraryBrowseResponse> {
    const response = await apiClient.get<LibraryBrowseResponse>('/library/components', { params });
    return response.data;
  },

  // Get specific shared component
  async getComponent(id: string): Promise<LibraryComponent & { component: Component }> {
    const response = await apiClient.get<LibraryComponent & { component: Component }>(
      `/library/components/${id}`
    );
    return response.data;
  },

  // Save component to library
  async saveComponent(
    pageId: string,
    data: CreateSharedComponentRequest
  ): Promise<{
    message: string;
    component: Omit<LibraryComponent, 'preview'>;
  }> {
    const response = await apiClient.post<{
      message: string;
      component: Omit<LibraryComponent, 'preview'>;
    }>(`/library/components?pageId=${pageId}`, data);
    return response.data;
  },

  // Update shared component metadata
  async updateComponent(
    id: string,
    data: UpdateSharedComponentRequest
  ): Promise<{
    message: string;
    component: Omit<LibraryComponent, 'preview'>;
  }> {
    const response = await apiClient.put<{
      message: string;
      component: Omit<LibraryComponent, 'preview'>;
    }>(`/library/components/${id}`, data);
    return response.data;
  },

  // Delete shared component
  async deleteComponent(id: string): Promise<{ message: string }> {
    const response = await apiClient.delete<{ message: string }>(`/library/components/${id}`);
    return response.data;
  },

  // Filter components by type
  async getComponentsByType(type: ComponentType, siteId?: string): Promise<LibraryBrowseResponse> {
    return this.browseComponents({ type, siteId, includePublic: true });
  },

  // Search components
  async searchComponents(search: string, siteId?: string): Promise<LibraryBrowseResponse> {
    return this.browseComponents({ search, siteId, includePublic: true });
  },

  // Filter by tags
  async getComponentsByTags(tags: string, siteId?: string): Promise<LibraryBrowseResponse> {
    return this.browseComponents({ tags, siteId, includePublic: true });
  },

  // Update library component content (the actual component data, not just metadata)
  async updateComponentContent(
    id: string,
    data: Record<string, any>
  ): Promise<{
    message: string;
    component: {
      id: string;
      name: string;
      type: ComponentType;
      updatedAt: string;
      data: any;
    };
  }> {
    const response = await apiClient.put<{
      message: string;
      component: {
        id: string;
        name: string;
        type: ComponentType;
        updatedAt: string;
        data: any;
      };
    }>(`/library/components/${id}/content`, { data });
    return response.data;
  },

  // Sync library component to all pages that use it
  async syncComponent(id: string): Promise<{
    message: string;
    libraryComponent: {
      id: string;
      name: string;
      type: ComponentType;
    };
    updatedPages: Array<{
      pageId: string;
      pageTitle: string;
      componentsUpdated: number;
    }>;
    totalComponentsUpdated: number;
  }> {
    const response = await apiClient.post<{
      message: string;
      libraryComponent: {
        id: string;
        name: string;
        type: ComponentType;
      };
      updatedPages: Array<{
        pageId: string;
        pageTitle: string;
        componentsUpdated: number;
      }>;
      totalComponentsUpdated: number;
    }>(`/library/components/${id}/sync`);
    return response.data;
  },

  // Get usage information for a library component
  async getComponentUsage(id: string): Promise<{
    libraryComponent: {
      id: string;
      name: string;
      type: ComponentType;
    };
    usage: {
      totalPages: number;
      totalInstances: number;
      pages: Array<{
        pageId: string;
        pageTitle: string;
        componentCount: number;
      }>;
    };
  }> {
    const response = await apiClient.get<{
      libraryComponent: {
        id: string;
        name: string;
        type: ComponentType;
      };
      usage: {
        totalPages: number;
        totalInstances: number;
        pages: Array<{
          pageId: string;
          pageTitle: string;
          componentCount: number;
        }>;
      };
    }>(`/library/components/${id}/usage`);
    return response.data;
  },
};
